// client/src/pages/BusinessProfile.js
import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.js';
import businessProfileService from '../services/businessProfileService.js';
import Button from '../components/ui/Button.js';

// Shared card class: solid deep blue in light mode, original translucent in dark mode
const cardClass = 'bg-royal-900 dark:bg-royal-800/40 border border-royal-700 dark:border-royal-600 rounded-xl';

function PhotoUpload({ currentPhotoUrl, onUpload, onRemove, uploading }) {
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        alert('Please select a JPEG, PNG, or WebP image.');
        return;
      }
      // Validate file size (2MB)
      if (file.size > 2 * 1024 * 1024) {
        alert('Image must be less than 2MB.');
        return;
      }
      onUpload(file);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  return (
    <div className="flex items-center gap-4">
      {/* Photo Preview */}
      <div className="relative flex-shrink-0">
        {currentPhotoUrl ? (
          <img
            src={currentPhotoUrl}
            alt="Profile"
            className="w-24 h-24 rounded-xl object-cover border-2 border-royal-600"
          />
        ) : (
          <div className="w-24 h-24 rounded-xl bg-royal-800 border-2 border-dashed border-royal-600 flex items-center justify-center">
            <svg className="w-8 h-8 text-royal-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
            </svg>
          </div>
        )}
        {uploading && (
          <div className="absolute inset-0 bg-black/50 rounded-xl flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>

      {/* Upload/Remove Buttons */}
      <div className="flex flex-col gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          onChange={handleFileSelect}
          className="hidden"
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="px-4 py-2.5 text-sm text-gold border border-gold/30 rounded-lg hover:bg-gold/10 transition-colors disabled:opacity-60"
        >
          {currentPhotoUrl ? 'Change Photo' : 'Upload Photo'}
        </button>
        {currentPhotoUrl && (
          <button
            onClick={onRemove}
            disabled={uploading}
            className="px-4 py-2.5 text-sm text-red-400 border border-red-400/30 rounded-lg hover:bg-red-400/10 transition-colors disabled:opacity-60"
          >
            Remove
          </button>
        )}
        <p className="text-royal-100 text-sm">JPEG, PNG, WebP. Max 2MB.</p>
      </div>
    </div>
  );
}

function LinkEditModal({ link, onSave, onClose, saving }) {
  const [name, setName] = useState(link?.name || '');
  const [url, setUrl] = useState(link?.url || '');
  const isNew = !link?.linkid;

  const handleSave = () => {
    if (!name.trim() || !url.trim()) return;
    onSave({ name: name.trim(), url: url.trim() });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div className="bg-royal-900 border border-royal-700 rounded-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-gold font-display text-xl">{isNew ? 'Add Link' : 'Edit Link'}</h3>
          <button
            onClick={onClose}
            className="text-royal-100 hover:text-white transition-colors text-2xl leading-none"
          >
            ×
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm text-royal-100 mb-1.5">Display Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., My Website"
              className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white placeholder-royal-200 focus:outline-none focus:border-gold/50 transition-colors"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm text-royal-100 mb-1.5">URL</label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://..."
              className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white placeholder-royal-200 focus:outline-none focus:border-gold/50 transition-colors"
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button
            onClick={handleSave}
            disabled={saving || !name.trim() || !url.trim()}
            className="flex-1"
          >
            {saving ? 'Saving...' : isNew ? 'Add Link' : 'Save Changes'}
          </Button>
          <button
            onClick={onClose}
            className="px-4 py-2.5 text-royal-100 border border-royal-600 rounded-lg text-base hover:text-white hover:border-royal-200 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function LinkRow({ link, onEdit, onDelete }) {
  return (
    <div className="flex items-center justify-between p-3 bg-royal-800/40 border border-royal-600 rounded-lg group hover:border-royal-200 transition-colors">
      <div className="min-w-0 flex-1">
        <p className="text-white text-base font-medium truncate">{link.name}</p>
        <p className="text-royal-100 text-sm truncate">{link.url}</p>
        {link.clickcount > 0 && (
          <p className="text-gold text-sm mt-0.5">{link.clickcount} click{link.clickcount !== 1 ? 's' : ''}</p>
        )}
      </div>
      <div className="flex gap-1 ml-3">
        <button
          onClick={() => onEdit(link)}
          className="p-2 text-royal-100 hover:text-gold rounded-lg hover:bg-royal-800/60 transition-colors"
          title="Edit"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </button>
        <button
          onClick={() => onDelete(link.linkid)}
          className="p-2 text-royal-100 hover:text-red-400 rounded-lg hover:bg-royal-800/60 transition-colors"
          title="Delete"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>
    </div>
  );
}

function ProfilePreview({ profile, links, userName }) {
  const displayName = profile?.displayname || userName || 'Your Name';
  const hasPhoto = profile?.photoimageurl;
  const hasTagline = profile?.tagline;
  const hasDescription = profile?.description;
  const hasLinks = links && links.length > 0;

  return (
    <div className={`${cardClass} p-6`}>
      <h3 className="text-gold font-display text-lg mb-4">Preview</h3>
      
      <div className="bg-royal-800/40 border border-royal-600 rounded-xl p-6">
        {/* Name */}
        <h2 className="text-gold font-display text-2xl text-center mb-2">{displayName}</h2>
        
        {/* Tagline */}
        {hasTagline && (
          <p className="text-royal-100 text-base text-center italic mb-4">{profile.tagline}</p>
        )}

        {/* Photo + Description */}
        {(hasPhoto || hasDescription) && (
          <div className={`flex flex-col md:flex-row gap-4 mb-4 ${!hasPhoto ? 'md:flex-col' : ''}`}>
            {hasPhoto && (
              <div className="flex-shrink-0 flex justify-center">
                <img
                  src={profile.photoimageurl}
                  alt={displayName}
                  className="w-32 h-32 md:w-40 md:h-40 rounded-xl object-cover"
                />
              </div>
            )}
            {hasDescription && (
              <div className="flex-1">
                <p className="text-royal-100 text-base leading-relaxed whitespace-pre-line">{profile.description}</p>
              </div>
            )}
          </div>
        )}

        {/* Links */}
        {hasLinks && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-4">
            {links.map((link) => (
              <a
                key={link.linkid}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block px-4 py-3 bg-gold/10 border border-gold/30 rounded-lg text-gold text-base font-medium text-center hover:bg-gold/20 transition-colors"
              >
                {link.name}
              </a>
            ))}
          </div>
        )}

        {!hasTagline && !hasPhoto && !hasDescription && !hasLinks && (
          <p className="text-royal-100 text-base text-center py-4">
            Fill in your profile details above to see a preview.
          </p>
        )}
      </div>
    </div>
  );
}

export default function BusinessProfile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [links, setLinks] = useState([]);
  const [maxLinks, setMaxLinks] = useState(4);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Form state
  const [isActive, setIsActive] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [tagline, setTagline] = useState('');
  const [description, setDescription] = useState('');

  // Modal state
  const [editingLink, setEditingLink] = useState(null); // null = closed, {} = new, {linkid, ...} = editing

  const userName = [user?.firstname, user?.lastname].filter(Boolean).join(' ');

  const fetchProfile = useCallback(async () => {
    try {
      const data = await businessProfileService.getProfile();
      setProfile(data.profile);
      setLinks(data.links || []);
      setMaxLinks(data.maxLinks || 4);

      if (data.profile) {
        setIsActive(data.profile.is_active || false);
        setDisplayName(data.profile.displayname || '');
        setTagline(data.profile.tagline || '');
        setDescription(data.profile.description || '');
      }
    } catch (err) {
      console.error('Fetch profile error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Auto-dismiss messages after 5 seconds
  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const handleUploadPhoto = async (file) => {
    setUploadingPhoto(true);
    setMessage({ type: '', text: '' });
    try {
      const data = await businessProfileService.uploadPhoto(file);
      setProfile(data.profile);
      setMessage({ type: 'success', text: 'Photo uploaded!' });
    } catch (err) {
      setMessage({ type: 'error', text: err.data?.error || 'Failed to upload photo' });
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleRemovePhoto = async () => {
    if (!window.confirm('Remove your profile photo?')) return;
    setUploadingPhoto(true);
    setMessage({ type: '', text: '' });
    try {
      await businessProfileService.removePhoto();
      setProfile({ ...profile, photoimageurl: null, photoimagefilename: null });
      setMessage({ type: 'success', text: 'Photo removed!' });
    } catch (err) {
      setMessage({ type: 'error', text: err.data?.error || 'Failed to remove photo' });
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const data = await businessProfileService.updateProfile({
        is_active: isActive,
        displayname: displayName || null,
        tagline: tagline || null,
        description: description || null,
      });
      setProfile(data.profile);
      setMessage({ type: 'success', text: 'Profile saved successfully!' });
    } catch (err) {
      setMessage({ type: 'error', text: err.data?.error || 'Failed to save profile' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (newValue) => {
    setIsActive(newValue);
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const data = await businessProfileService.updateProfile({
        is_active: newValue,
        displayname: displayName || null,
        tagline: tagline || null,
        description: description || null,
      });
      setProfile(data.profile);
      setMessage({ type: 'success', text: newValue ? 'Profile activated!' : 'Profile deactivated!' });
    } catch (err) {
      setIsActive(!newValue); // Revert on error
      setMessage({ type: 'error', text: err.data?.error || 'Failed to update profile' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveLink = async (data) => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      if (editingLink.linkid) {
        // Update existing
        const result = await businessProfileService.updateLink(editingLink.linkid, data);
        setLinks(links.map((l) => (l.linkid === editingLink.linkid ? result.link : l)));
        setMessage({ type: 'success', text: 'Link updated!' });
      } else {
        // Add new
        const result = await businessProfileService.addLink(data.name, data.url);
        setLinks([...links, result.link]);
        setMessage({ type: 'success', text: 'Link added!' });
      }
      setEditingLink(null);
    } catch (err) {
      setMessage({ type: 'error', text: err.data?.error || 'Failed to save link' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLink = async (linkid) => {
    if (!window.confirm('Delete this link?')) return;
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      await businessProfileService.deleteLink(linkid);
      setLinks(links.filter((l) => l.linkid !== linkid));
      setMessage({ type: 'success', text: 'Link deleted!' });
    } catch (err) {
      setMessage({ type: 'error', text: err.data?.error || 'Failed to delete link' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-royal-600 border-t-gold rounded-full animate-spin" />
      </div>
    );
  }

  // Build preview profile object
  const previewProfile = {
    displayname: displayName,
    tagline,
    description,
    photoimageurl: profile?.photoimageurl,
  };

  return (
    <div className="min-h-screen bg-cream dark:bg-bg-dark transition-colors duration-300">
      <div className="max-w-4xl mx-auto px-4 py-8">

        {/* Dashboard Back Link */}
        <div className="mb-5">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gold text-royal-900 font-semibold text-base hover:bg-gold-light transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Dashboard
          </Link>
        </div>

        <div className="mb-6">
          <h1 className="font-display text-2xl text-gold-dark dark:text-gold">Business Profile</h1>
          <p className="text-royal-600 dark:text-royal-100 text-base mt-1">
            Customize how you appear to eCard recipients when they click "About the Sender"
          </p>
        </div>

        {message.text && (
          <div className={`mb-6 p-4 rounded-lg text-base transition-opacity ${
            message.type === 'success' ? 'bg-green-900/30 border border-green-600 text-green-400' : 'bg-red-900/30 border border-red-600 text-red-400'
          }`}>
            {message.text}
          </div>
        )}

        {/* Link Edit Modal */}
        {editingLink !== null && (
          <LinkEditModal
            link={editingLink}
            onSave={handleSaveLink}
            onClose={() => setEditingLink(null)}
            saving={saving}
          />
        )}

        <div className="space-y-6">
          {/* Profile Active Toggle */}
          <div className={`${cardClass} p-5`}>
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <p className="text-white text-base font-medium">Profile Active</p>
                <p className="text-royal-100 text-sm mt-1">
                  When enabled, recipients can see your profile on your eCards
                </p>
              </div>
              <div className="relative flex-shrink-0 ml-4">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => handleToggleActive(e.target.checked)}
                  disabled={saving}
                  className="sr-only"
                />
                <div className={`w-14 h-8 rounded-full transition-colors ${isActive ? 'bg-gold' : 'bg-royal-600'} ${saving ? 'opacity-60' : ''}`}>
                  <div className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow-md transition-transform ${isActive ? 'translate-x-7' : 'translate-x-1'}`} />
                </div>
              </div>
            </label>
          </div>

          {/* Profile Details */}
          <div className={`${cardClass} p-5 space-y-4`}>
            <h2 className="text-white text-lg font-semibold">Profile Details</h2>

            {/* Profile Photo */}
            <div>
              <label className="block text-sm text-royal-100 mb-2">Profile Photo</label>
              <PhotoUpload
                currentPhotoUrl={profile?.photoimageurl}
                onUpload={handleUploadPhoto}
                onRemove={handleRemovePhoto}
                uploading={uploadingPhoto}
              />
            </div>

            {/* Display Name and Tagline - side by side on desktop */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-royal-100 mb-1.5">Display Name</label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder={userName || 'Your Name'}
                  className="w-full px-3 py-2.5 bg-royal-900/60 border border-royal-600 rounded-lg text-base text-white placeholder-royal-200 focus:outline-none focus:border-gold/50 transition-colors"
                />
                <p className="text-royal-100 text-sm mt-1">Leave blank to use your account name</p>
              </div>

              <div>
                <label className="block text-sm text-royal-100 mb-1.5">Tagline</label>
                <input
                  type="text"
                  value={tagline}
                  onChange={(e) => setTagline(e.target.value)}
                  placeholder="e.g., I sell luxury homes in Miami"
                  className="w-full px-3 py-2.5 bg-royal-900/60 border border-royal-600 rounded-lg text-base text-white placeholder-royal-200 focus:outline-none focus:border-gold/50 transition-colors"
                />
              </div>
            </div>

            {/* Description - full width */}
            <div>
              <label className="block text-sm text-royal-100 mb-1.5">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Tell recipients more about yourself or your business..."
                rows={6}
                className="w-full px-3 py-2.5 bg-royal-900/60 border border-royal-600 rounded-lg text-base text-white placeholder-royal-200 focus:outline-none focus:border-gold/50 resize-y transition-colors"
              />
            </div>

            <Button onClick={handleSaveProfile} disabled={saving}>
              {saving ? 'Saving...' : 'Save Profile'}
            </Button>
          </div>

          {/* Profile Links - full width below */}
          <div className={`${cardClass} p-5 space-y-4`}>
            <div className="flex items-center justify-between">
              <h2 className="text-white text-lg font-semibold">Profile Links</h2>
              <span className="text-royal-100 text-sm">{links.length} / {maxLinks}</span>
            </div>
            <p className="text-royal-100 text-sm">
              Add links to your website, social media, or contact pages. These will appear on your profile.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {links.map((link) => (
                <LinkRow
                  key={link.linkid}
                  link={link}
                  onEdit={setEditingLink}
                  onDelete={handleDeleteLink}
                />
              ))}
              
              <button
                onClick={() => setEditingLink({})}
                disabled={links.length >= maxLinks}
                className="p-4 border-2 border-dashed border-royal-600 rounded-lg text-royal-100 text-base font-medium hover:text-gold hover:border-gold/50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {links.length >= maxLinks ? 'Maximum reached' : '+ Add Link'}
              </button>
            </div>
          </div>

          {/* Preview - Full width at bottom */}
          <ProfilePreview
            profile={previewProfile}
            links={links}
            userName={userName}
          />
        </div>
      </div>
    </div>
  );
}